import { z } from "zod"
import { getNursePatients } from "./derivedAssignments"

// Constants for validation
const MINUTES_IN_DAY = 1440

// Basic reusable schemas
const MinutesFromMidnight = z.number().int().min(0).max(MINUTES_IN_DAY)

const NurseRole = z.enum(["RN", "LVN", "Charge"])

const VisitStatus = z.enum([
	"Completed",
	"Scheduled",
	"Checked In",
	"Cancellation",
	"Add on",
	"Ongoing",
	"No Show"
])

const ChairType = z
	.object({
		Bed: z.boolean(),
		Chair: z.boolean(),
		Gurney: z.boolean()
	})
	.refine(
		data => {
			const trueCount = [data.Bed, data.Chair, data.Gurney].filter(Boolean).length
			return trueCount === 1
		},
		{ message: "Exactly one chair type must be true" }
	)

// Patient Schema
export const PatientSchema = z
	.object({
		patientId: z.number(),
		patientName: z.string(),
		patientMRN: z.string(),
		infusionType: z.string(),
		scheduledStartTime: MinutesFromMidnight,
		scheduledEndTime: MinutesFromMidnight,
		length: z.number(),
		arrivalTime: MinutesFromMidnight.nullable(),
		dueTime: MinutesFromMidnight,
		actualStartTime: MinutesFromMidnight.nullable(),
		actualEndTime: MinutesFromMidnight.nullable(),
		actualDuration: z.number().nullable(),
		bookedDate: z.string(), // Consider using a more specific date validation
		visitStatus: VisitStatus,
		department: z.string().nullable(),
		clinicProvider: z.string(),
		clinicAppointmentTime: MinutesFromMidnight.nullable(),
		acuity: z.number().int().min(1).max(5),
		assignedChair: z.number().nullable(),
		assignedNurse: z.string().nullable(),
		transferredBy: z.string().nullable(),
		readyTime: MinutesFromMidnight,
		internalStatus: z.string(),
		linkedClinicAppointment: z.string().nullable(),
		cancellationConsideredForReassignment: z.boolean().optional(),
		shownCancelledPatient: z.boolean().optional(),
		pendingChanges: z.boolean().nullable(),
		notes: z.string().nullable(),
		reasonForVisit: z.string().optional(),
		location: z.string().optional()
	})
	.passthrough()
	.refine(
		data => {
			if (data.scheduledEndTime <= data.scheduledStartTime) return false
			if (
				data.actualEndTime &&
				data.actualStartTime &&
				data.actualEndTime <= data.actualStartTime
			)
				return false
			if (data.arrivalTime && data.actualStartTime && data.actualStartTime < data.arrivalTime)
				return false
			return true
		},
		{ message: "Time sequence constraints violated" }
	)

export const EditedPatientSchema = z
	.object({
		patientId: z.number(),
		assignedNurse: z.boolean(),
		assignedChair: z.boolean(),
		acuity: z.boolean(),
		cancellationConsideredForReassignment: z.boolean(),
		shownCancelledPatient: z.boolean(),
		pendingChanges: z.boolean(),
		notes: z.boolean()
	})
	.passthrough()

// Nurse Schema
export const NurseSchema = z
	.object({
		id: z.number(),
		nurseId: z.string(),
		nurseName: z.string(),
		nurseRole: NurseRole,
		nurseEmail: z.string().email(),
		scheduledStartTime: MinutesFromMidnight,
		scheduledEndTime: MinutesFromMidnight,
		actualStartTime: MinutesFromMidnight.nullable(),
		actualEndTime: MinutesFromMidnight.nullable(),
		actualLunchBreakStart: MinutesFromMidnight.nullable(),
		actualLunchBreakEnd: MinutesFromMidnight.nullable(),
		scheduledLunchBreakStart: MinutesFromMidnight.nullable(),
		scheduledLunchBreakEnd: MinutesFromMidnight.nullable(),
		transferredPatients: z.array(z.number()).optional()
	})
	.passthrough()

const EditedNurseSchema = z
	.object({
		nurseId: z.string(),
		added: z.boolean(),
		deleted: z.boolean(),
		name: z.boolean(),
		scheduledStartTime: z.boolean(),
		scheduledEndTime: z.boolean(),
		scheduledLunchStartTime: z.boolean(),
		scheduledLunchEndTime: z.boolean()
	})
	.passthrough()

// Chair Schema
export const ChairSchema = z
	.object({
		chairId: z.number(),
		chairName: z.string(),
		chairType: ChairType
	})
	.passthrough()

export const AcuityMixSchema = z.record(z.string(), z.number().int())

// Schedule Schema
export const ScheduleSchema = z
	.object({
		patientSchedule: z.array(PatientSchema).optional(),
		nurseSchedule: z.array(NurseSchema).optional(),
		chairSchedule: z.array(ChairSchema).optional(),
		isLocked: z.boolean().optional(),
		triedToLock: z.boolean().optional(),
		chargeNurse: NurseSchema.nullable(),
		acuityMix: AcuityMixSchema.optional(),
		utilizationCurve: z.array(z.number()).optional()
	})
	.passthrough()

const applyLogicRefinements = object =>
	object
		.refine(
			data =>
				!data.chargeNurse ||
				getNursePatients(data.patientSchedule, data.chargeNurse.nurseId).length === 0,
			{ message: "Charge nurse must not have assigned patients" }
		)
		.refine(
			data => {
				for (const patient of data.patientSchedule) {
					if (patient.assignedChair && !patient.assignedNurse)
						throw new Error(
							`Patient ${patient.patientId} is assigned to chair ${patient.assignedChair} but not assigned to a nurse`
						)
					if (patient.assignedNurse && !patient.assignedChair)
						throw new Error(
							`Patient ${patient.patientId} is assigned to nurse ${patient.assignedNurse} but not assigned to a chair`
						)
				}
				return true
			},
			{ message: err => err.message }
		)
		.refine(
			data => {
				for (const patient of data.patientSchedule) {
					if (!patient.assignedChair || !patient.assignedNurse) continue

					const chair = data.chairSchedule.find(c => c.chairId === patient.assignedChair)
					if (!chair)
						throw new Error(
							`Patient ${patient.patientId} is assigned to chair ${patient.assignedChair} but not found in chair schedule`
						)

					const nurse = data.nurseSchedule.find(n => n.nurseId === patient.assignedNurse)
					if (!nurse)
						throw new Error(
							`Patient ${patient.patientId} is assigned to nurse ${patient.assignedNurse} but not found in nurse schedule`
						)
				}
				return true
			},
			{ message: err => err.message }
		)

export const ScheduleSchemaState = applyLogicRefinements(
	ScheduleSchema.extend({
		status: z.enum(["idle", "loading", "succeeded", "failed"]),
		error: z.string().nullable(),
		isEditing: z.boolean(),
		documentId: z.string().nullable(),
		openTime: z.string(),
		closeTime: z.string(),
		editedPatients: z.array(EditedPatientSchema),
		editedNurses: z.array(EditedNurseSchema),

		sftpReassignment: z.array(z.string()).optional()
	})
)

export const SandboxSchemaState = applyLogicRefinements(
	ScheduleSchema.extend({
		status: z.enum(["idle", "loading", "succeeded", "failed"]),
		error: z.string().nullable(),
		documentId: z.string().nullable(),
		openTime: z.string(),
		closeTime: z.string(),
		isSandboxMode: z.boolean()
	})
)

export const TomorrowSchemaState = applyLogicRefinements(
	ScheduleSchema.extend({
		status: z.enum(["idle", "loading", "succeeded", "failed"]),
		error: z.string().nullable(),
		isEditing: z.boolean(),
		documentId: z.string().nullable(),
		openTime: z.string(),
		closeTime: z.string(),
		editedPatients: z.array(EditedPatientSchema),
		editedNurses: z.array(EditedNurseSchema),
		sftpReassignment: z.array(z.string()).optional(),
		isTomorrowMode: z.boolean()
	})
)
